/** @jsx jsx */
import { useState } from 'react';
import { useDirection, useIconProvider } from '@front10/utils';
import { jsx } from '@theme-ui/core';
import { Styled } from './types';
import clsx from 'clsx';

export type IconName = string;

interface Props {
  icon?: IconName | string;
  animation?: 'mirror' | 'infinite';
  size?: number | string;
  paths?: (string | object)[];
  viewBox?: string;
  rtlFlip?: boolean;
}

const renderPaths = (svgPaths?: (string | object)[]) => {
  if (svgPaths) {
    return svgPaths.map((item, key: number) =>
      typeof item === 'string' ? <path key={key} d={item} /> : <path key={key} {...item} />
    );
  }
  return null;
};

const renderSprite = (icon: string) => {
  return <use xlinkHref={`#${icon}`}></use>;
};

export type IconProps = Styled<Props & Omit<JSX.IntrinsicElements['svg'], 'ref'>>;

export function Icon({
  icon = 'broken-heart',
  animation,
  size = 16,
  viewBox = '0 0 1024 1024',
  paths = [],
  rtlFlip = true,
  fill = 'currentcolor',
  sx,
  ...props
}: IconProps) {
  const direction = useDirection();
  const [aniActive, setAnimationActive] = useState(false);
  let newViewBox = viewBox;
  let newIcon = icon;

  let enableSprite = false;
  let finalPath = paths;
  const { config, icons } = useIconProvider();
  if (icons && icons?.length > 0) {
    if (config && config.enableSprite) {
      enableSprite = true;
    }
    if (config && config.id) {
      newIcon = `${config.id}${icon}`;
    }
    const iconFound = icons.find((item: $TSFixMe) => item.icon === icon) as $TSFixMe;
    if (iconFound) {
      if (!config.enableSprite) {
        finalPath = iconFound.paths;
      }
      if (iconFound.viewBox) {
        newViewBox = iconFound.viewBox && iconFound.viewBox;
      }
    } else {
      console.warn(`Please add ${icon} to the iconsProvider. Searching and loading embedded icon.`);
    }
  }

  const rotateIcon = () => {
    setAnimationActive(!aniActive);
  };
  return (
    <svg
      onClick={() => animation !== 'infinite' && rotateIcon()}
      className={clsx(
        `fr-icon fr-icon-${icon}`,
        animation === 'infinite' && `fr-icon-animation-${animation}`,
        aniActive && `fr-icon-animation-${animation}-active`
      )}
      width={size}
      height={size}
      data-fr-icon={icon}
      viewBox={newViewBox}
      sx={{
        fill,
        transform: rtlFlip && direction === 'rtl' ? 'scaleX(-1)' : undefined,
        ...sx
      }}
      {...props}
    >
      {enableSprite && renderSprite(newIcon)}
      {!enableSprite && renderPaths(finalPath)}
    </svg>
  );
}
